import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ApiError, api } from "../../../api/client.js";

/**
 * FIX-067: "Publish to Telegram" for approved images (a whole request, a selection, or a Bundle). The channel is chosen by the server from the
 * request's persona — this screen never sends a chat id. Nothing is posted until "Publish now" is clicked in the preview.
 */
export type TelegramPublicationStatus = "preview" | "sending" | "sent" | "partially_sent" | "failed" | "uncertain" | "skipped" | "cancelled";

export interface TelegramPublicationView {
  readonly id: string;
  readonly channel: { readonly key: string; readonly title: string; readonly username: string };
  readonly trigger: "manual" | "auto";
  readonly autoLocalDate: string | null;
  readonly sourceKind: "attempts" | "batch" | "bundle";
  readonly sourceId: string;
  readonly batchId: string;
  readonly attemptIds: readonly string[];
  readonly imageUrls: readonly string[];
  readonly contentMode: string;
  readonly captionText: string | null;
  readonly captionOrigin: string;
  readonly captionError: string | null;
  readonly republishOf: string | null;
  readonly status: TelegramPublicationStatus;
  readonly error: string | null;
  readonly plannedParts: readonly (readonly string[])[];
  readonly parts: readonly { readonly partNumber: number; readonly count: number; readonly status: string; readonly messageLink: string | null; readonly error: string | null }[];
  readonly links: readonly string[];
  readonly alreadyPublishedAttemptIds: readonly string[];
  readonly createdAt: string;
  readonly sentAt: string | null;
}

const LIVE: readonly TelegramPublicationStatus[] = ["sending", "sent", "partially_sent", "uncertain"];
export const TELEGRAM_STATUS_LABEL: Record<TelegramPublicationStatus, string> = {
  preview: "Preview",
  sending: "Sending…",
  sent: "Published",
  partially_sent: "Partly published",
  failed: "Not published",
  uncertain: "Unclear — check the channel",
  skipped: "Skipped",
  cancelled: "Cancelled",
};
const CAPTION_LIMIT = 1000;

function errorText(e: unknown): string {
  return e instanceof ApiError ? e.message : "That action failed.";
}
function newRequestId(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : `tg-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function TelegramPublishButton(props: {
  readonly tenantId: string;
  readonly sourceKind: "attempts" | "batch" | "bundle";
  readonly sourceId: string;
  readonly attemptIds?: readonly string[];
  readonly csrfToken: string | null;
  readonly disabled?: boolean;
  readonly label?: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button disabled={props.disabled === true} onClick={() => setOpen(true)} data-testid={`telegram-publish-${props.sourceKind}-${props.sourceId}`}>
        {props.label ?? "Publish to Telegram"}
      </button>
      {open && <TelegramPublishModal {...props} onClose={() => setOpen(false)} />}
    </>
  );
}

function TelegramPublishModal(props: {
  readonly tenantId: string;
  readonly sourceKind: "attempts" | "batch" | "bundle";
  readonly sourceId: string;
  readonly attemptIds?: readonly string[];
  readonly csrfToken: string | null;
  readonly onClose: () => void;
}) {
  const base = `/images/${encodeURIComponent(props.tenantId)}/telegram-publications`;
  const requestId = useRef(newRequestId());
  const [pub, setPub] = useState<TelegramPublicationView | null>(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmAgain, setConfirmAgain] = useState(false);

  const show = (p: TelegramPublicationView) => {
    setPub(p);
    setDraft(p.captionText ?? "");
  };
  async function run(action: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }
  async function startPreview(republishOf: string | null) {
    await run(async () => {
      if (republishOf !== null) requestId.current = newRequestId();
      const r = await api.post<{ publication: TelegramPublicationView }>(`${base}/preview`, props.csrfToken, {
        sourceKind: props.sourceKind,
        sourceId: props.sourceId,
        ...(props.attemptIds ? { attemptIds: props.attemptIds } : {}),
        clientRequestId: requestId.current,
        ...(republishOf !== null ? { republishOf } : {}),
      });
      show(r.publication);
    });
  }
  useEffect(() => {
    void startPreview(null);
  }, []);

  const id = pub?.id ?? "";
  const captionDirty = pub !== null && draft.trim() !== (pub.captionText ?? "");
  const alreadyPublished = pub !== null && pub.republishOf === null && pub.alreadyPublishedAttemptIds.length > 0;

  async function publishAgain() {
    await run(async () => {
      const r = await api.get<{ publications: TelegramPublicationView[] }>(`${base}?batchId=${encodeURIComponent(pub!.batchId)}&limit=200`);
      const prior = r.publications.find((p) => LIVE.includes(p.status) && p.attemptIds.some((a) => pub!.alreadyPublishedAttemptIds.includes(a)));
      if (!prior) throw new Error("The earlier post was not found.");
      await api.post(`${base}/${encodeURIComponent(id)}/cancel`, props.csrfToken);
      setPub(null);
      setBusy(false);
      await startPreview(prior.id);
    });
  }

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" aria-label="Publish to Telegram" data-testid="telegram-publish-modal">
      <div className="modal-content card" style={{ maxWidth: 760 }}>
        <h3>Publish to Telegram</h3>
        {pub === null ? (
          <p className="muted">{error ?? "Preparing the preview…"}</p>
        ) : (
          <>
            <p>
              Channel: <strong>{pub.channel.title}</strong> (@{pub.channel.username}) — {pub.attemptIds.length} image{pub.attemptIds.length === 1 ? "" : "s"}
              {pub.plannedParts.length > 1 ? `, sent as ${pub.plannedParts.length} posts` : ""}.
            </p>
            <div className="image-bundle-strip">
              {pub.imageUrls.map((u) => (
                <img key={u} src={u} alt="To publish" loading="lazy" />
              ))}
            </div>
            {alreadyPublished && (
              <div className="card" data-testid="telegram-already-published">
                <strong>Already published.</strong> Some of these images are already in the channel.{" "}
                {pub.status === "preview" && (
                  <button disabled={busy} onClick={() => void publishAgain()}>
                    Publish again…
                  </button>
                )}
              </div>
            )}
            {pub.status === "preview" ? (
              <>
                <label>
                  Caption
                  <textarea value={draft} maxLength={CAPTION_LIMIT} rows={4} style={{ width: "100%" }} onChange={(e) => setDraft(e.target.value)} data-testid="telegram-caption" />
                </label>
                <div className="muted">
                  {draft.length}/{CAPTION_LIMIT}
                  {pub.captionText === null ? " — a caption is required. Write one." : ""}
                  {pub.captionError ? ` (${pub.captionError})` : ""}
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
                  <button disabled={busy || !captionDirty || draft.trim().length === 0} onClick={() => void run(async () => show((await api.post<{ publication: TelegramPublicationView }>(`${base}/${encodeURIComponent(id)}/caption`, props.csrfToken, { text: draft })).publication))}>
                    Save caption
                  </button>
                </div>
                {pub.republishOf !== null && (
                  <label style={{ display: "block", marginTop: 8 }}>
                    <input type="checkbox" checked={confirmAgain} onChange={(e) => setConfirmAgain(e.target.checked)} data-testid="telegram-confirm-again" /> Yes, publish these images again.
                  </label>
                )}
                <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                  <button
                    className="primary"
                    data-testid="telegram-publish-now"
                    disabled={busy || pub.captionText === null || captionDirty || alreadyPublished || (pub.republishOf !== null && !confirmAgain)}
                    onClick={() => void run(async () => show((await api.post<{ publication: TelegramPublicationView }>(`${base}/${encodeURIComponent(id)}/confirm`, props.csrfToken, { captionText: pub.captionText, confirmRepublish: confirmAgain })).publication))}
                  >
                    Publish now
                  </button>
                  <button
                    disabled={busy}
                    onClick={() =>
                      void run(async () => {
                        await api.post(`${base}/${encodeURIComponent(id)}/cancel`, props.csrfToken);
                        props.onClose();
                      })
                    }
                  >
                    Cancel
                  </button>
                </div>
                {captionDirty && <p className="muted">Save the caption before publishing.</p>}
              </>
            ) : (
              <TelegramPublicationResult pub={pub} busy={busy} onResolve={(action) => void run(async () => show((await api.post<{ publication: TelegramPublicationView }>(`${base}/${encodeURIComponent(id)}/resolve`, props.csrfToken, { action })).publication))} />
            )}
            {error && (
              <p className="error" data-testid="telegram-publish-error">
                {error}
              </p>
            )}
            {pub.status !== "preview" && (
              <button onClick={props.onClose} style={{ marginTop: 12 }}>
                Close
              </button>
            )}
          </>
        )}
        {pub === null && (
          <button onClick={props.onClose} style={{ marginTop: 12 }}>
            Close
          </button>
        )}
      </div>
    </div>
  );
}

export function TelegramPublicationResult({ pub, busy, onResolve }: { readonly pub: TelegramPublicationView; readonly busy: boolean; readonly onResolve?: (action: "mark_sent" | "mark_not_sent" | "continue") => void }) {
  return (
    <div data-testid="telegram-publication-result">
      <p>
        <strong>{TELEGRAM_STATUS_LABEL[pub.status]}</strong>
        {pub.error ? ` — ${pub.error}` : ""}
      </p>
      {pub.captionText && <p className="muted" style={{ whiteSpace: "pre-wrap" }}>{pub.captionText}</p>}
      {pub.links.length > 0 && (
        <ul>
          {pub.links.map((l) => (
            <li key={l}>
              <a href={l} target="_blank" rel="noreferrer">
                {l}
              </a>
            </li>
          ))}
        </ul>
      )}
      {pub.status === "uncertain" && onResolve && (
        <div>
          <p className="muted">It is not known whether Telegram received the last post. Check the channel, then choose:</p>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button disabled={busy} onClick={() => onResolve("mark_sent")}>
              It is in the channel
            </button>
            <button disabled={busy} onClick={() => onResolve("mark_not_sent")}>
              It is not in the channel
            </button>
            <button disabled={busy} onClick={() => onResolve("continue")}>
              Send the remaining posts
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/** The list of Telegram posts (manual and automatic), with links. Optionally limited to one request. */
export function TelegramPublicationsList({ tenantId, batchId, refreshKey = 0, title = "Telegram posts", allPostsHref, trigger }: { readonly tenantId: string; readonly batchId?: string; readonly refreshKey?: number; readonly title?: string; readonly allPostsHref?: string; readonly trigger?: "auto" | "manual" }) {
  const [items, setItems] = useState<TelegramPublicationView[] | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  useEffect(() => {
    let cancelled = false;
    api
      .get<{ publications: TelegramPublicationView[] }>(`/images/${encodeURIComponent(tenantId)}/telegram-publications?limit=100${batchId ? `&batchId=${encodeURIComponent(batchId)}` : ""}${trigger ? `&trigger=${trigger}` : ""}`)
      .then((r) => {
        if (!cancelled) setItems(r.publications.filter((p) => p.status !== "preview" && p.status !== "cancelled"));
      })
      .catch(() => {
        if (!cancelled) setUnavailable(true);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, batchId, refreshKey, trigger]);
  if (unavailable) return null;
  return (
    <div className="card" data-testid={trigger ? `telegram-publications-${trigger}` : "telegram-publications"}>
      <h3>{title}</h3>
      {allPostsHref && (
        <p>
          <Link to={allPostsHref}>All Telegram posts</Link>
        </p>
      )}
      {items === null ? (
        <p className="muted">Loading…</p>
      ) : items.length === 0 ? (
        <p className="muted">Nothing published yet.</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>When</th>
              <th>Channel</th>
              <th>How</th>
              <th>Images</th>
              <th>Status</th>
              <th>Link</th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} data-testid={`telegram-publication-${p.id}`}>
                <td>{new Date(p.sentAt ?? p.createdAt).toLocaleString()}</td>
                <td>{p.channel.title}</td>
                <td>{p.trigger === "auto" ? "Automatic" : "Manual"}</td>
                <td>{p.attemptIds.length}</td>
                <td>
                  {TELEGRAM_STATUS_LABEL[p.status]}
                  {p.error ? ` — ${p.error}` : ""}
                </td>
                <td>
                  {p.links[0] ? (
                    <a href={p.links[0]} target="_blank" rel="noreferrer">
                      Open
                    </a>
                  ) : (
                    "—"
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
